import { PERMISSIONS } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { api, errorText } from '../../api/client';
import type { StaffUser, ZaloOaStatus } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

interface NotificationRow {
  id: string;
  status: 'pending' | 'sent' | 'failed' | 'skipped';
  error_message: string | null;
  created_at: string;
  attempts: number;
  recipient: { full_name: string } | null;
  rental_request: { id: string; request_code: string } | null;
}
const NOTI = {
  sent: ['green', 'Đã gửi'],
  failed: ['red', 'Lỗi'],
  skipped: ['default', 'Bỏ qua'],
  pending: ['blue', 'Đang gửi'],
} as const;

/** D-12: kết nối OA để gửi tin tư vấn cho nhân viên. App Secret chỉ nằm ở biến môi trường máy chủ. */
export function ZaloOaPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.SETTINGS_MANAGE);
  const [form] = Form.useForm();
  const [testForm] = Form.useForm();
  const st = useQuery({
    queryKey: ['zalo-oa'],
    queryFn: () => api.get<ZaloOaStatus>('/admin/integrations/zalo-oa'),
  });
  const linked = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api.page<StaffUser>('/admin/users', { limit: 100 })).items,
    enabled: can(PERMISSIONS.USERS_READ),
  });
  const notis = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.page<NotificationRow>('/admin/notifications', { limit: 20 }),
  });
  const s = st.data;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      <Card
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            Kết nối Zalo OA
          </Typography.Title>
        }
        loading={st.isLoading}
      >
        {s && (
          <>
            {!s.server_configured && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Máy chủ chưa cấu hình ZALO_APP_ID / ZALO_APP_SECRET / ENCRYPTION_KEY"
                description="Quản trị máy chủ đặt các biến môi trường này (không nhập App Secret ở đây). Trong lúc chưa kết nối, yêu cầu mới vẫn hiện ở chuông trên Admin."
              />
            )}
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="OA">{s.oa_id ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Trạng thái">
                {s.connected ? (
                  <Tag color="green">Đã kết nối</Tag>
                ) : (
                  <Tag color="red">Chưa kết nối</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Access token hết hạn">
                {s.access_expires_at ? dayjs(s.access_expires_at).format('DD/MM/YYYY HH:mm') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Làm mới gần nhất">
                {s.last_refreshed_at ? dayjs(s.last_refreshed_at).format('DD/MM/YYYY HH:mm') : '—'}
              </Descriptions.Item>
              {s.last_error && (
                <Descriptions.Item label="Lỗi gần nhất">
                  <Typography.Text type="danger">{s.last_error}</Typography.Text>
                </Descriptions.Item>
              )}
            </Descriptions>
            {canManage && s.server_configured && (
              <Form
                form={form}
                layout="vertical"
                style={{ marginTop: 16, maxWidth: 640 }}
                onFinish={async (v) => {
                  try {
                    await api.put('/admin/integrations/zalo-oa/token', {
                      refresh_token: v.refresh_token.trim(),
                    });
                    form.resetFields();
                    message.success('Đã kết nối OA');
                    void qc.invalidateQueries({ queryKey: ['zalo-oa'] });
                  } catch (e) {
                    message.error(errorText(e));
                  }
                }}
              >
                <Form.Item
                  name="refresh_token"
                  label="Refresh token của OA"
                  rules={[{ required: true, min: 20 }]}
                  extra="Lấy từ bước cấp quyền OA cho ứng dụng trên developers.zalo.me. Token được mã hóa khi lưu và tự làm mới; không hiển thị lại."
                >
                  <Input.Password autoComplete="off" />
                </Form.Item>
                <Button type="primary" htmlType="submit">
                  Lưu & kiểm tra
                </Button>
              </Form>
            )}
          </>
        )}
      </Card>

      {canManage && s?.connected && (
        <Card title="Gửi tin thử">
          <Form
            form={testForm}
            layout="inline"
            onFinish={async (v) => {
              try {
                const r = await api.post<{ ok: boolean; code?: string; message?: string }>(
                  '/admin/integrations/zalo-oa/test',
                  { user_id: v.user_id },
                );
                if (r.ok) message.success('Đã gửi tin thử');
                else message.error(`Zalo từ chối: ${r.message ?? r.code}`);
              } catch (e) {
                message.error(errorText(e));
              }
            }}
          >
            <Form.Item name="user_id" rules={[{ required: true, message: 'Chọn nhân viên' }]}>
              <Select
                style={{ width: 280 }}
                placeholder="Nhân viên đã liên kết Zalo"
                options={(linked.data ?? [])
                  .filter((u) => u.zalo_oa_user_id)
                  .map((u) => ({ value: u.zalo_oa_user_id, label: u.full_name }))}
              />
            </Form.Item>
            <Button htmlType="submit">Gửi</Button>
          </Form>
          <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
            Liên kết tài khoản Zalo của nhân viên ở mục <Link to="/users">Nhân viên</Link>.
          </Typography.Paragraph>
        </Card>
      )}

      <Card title="Nhật ký gửi thông báo">
        <Table<NotificationRow>
          rowKey="id"
          size="small"
          loading={notis.isLoading}
          dataSource={notis.data?.items}
          pagination={false}
          columns={[
            { title: 'Thời gian', render: (_, n) => dayjs(n.created_at).format('DD/MM HH:mm') },
            {
              title: 'Yêu cầu',
              render: (_, n) =>
                n.rental_request ? (
                  <Link to={`/requests/${n.rental_request.id}`}>
                    {n.rental_request.request_code}
                  </Link>
                ) : (
                  '—'
                ),
            },
            { title: 'Người nhận', render: (_, n) => n.recipient?.full_name ?? '—' },
            {
              title: 'Kết quả',
              render: (_, n) => <Tag color={NOTI[n.status][0]}>{NOTI[n.status][1]}</Tag>,
            },
            { title: 'Số lần', dataIndex: 'attempts', align: 'right' },
            { title: 'Lỗi', dataIndex: 'error_message', render: (v) => v ?? '' },
          ]}
        />
      </Card>
    </Space>
  );
}
